/* eslint @typescript-eslint/explicit-function-return-type: 1, @typescript-eslint/no-explicit-any: 1 -- TODO fix types */
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  BehaviorSubject,
  catchError,
  filter,
  finalize,
  Observable,
  retry,
  switchMap,
  take,
  tap,
  throwError,
  timer,
} from 'rxjs';

import { AuthorizationService, UserService } from '@hosty-app/services';

// Sentinel emitted to unblock queued requests when refresh fails
const REFRESH_FAILED = 'REFRESH_FAILED' as const;

@Injectable()
export class RefreshTokenInterceptor implements HttpInterceptor {
  private refreshTokenInProgress = false;
  private refreshTokenSubject: BehaviorSubject<string | null>;

  constructor(
    public authorizationService: AuthorizationService,
    public userService: UserService,
    public router: Router,
  ) {
    this.refreshTokenSubject = new BehaviorSubject<string | null>(null);
  }

  public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (request.url.includes('refresh') || request.url.includes('login')) {
          if (request.url.includes('refresh')) {
            // Only wipe tokens and redirect when the refresh endpoint itself returned
            // an auth error — NOT on network failures (status 0) which happen transiently
            // during WiFi ↔ mobile data switches.
            if (error.status === 401 || error.status === 403) {
              this.refreshTokenSubject.next(REFRESH_FAILED);
              this.authorizationService.cleanTokens();
              this.router.navigateByUrl('/login');
            } else {
              // Network error: unblock queued requests so they fail cleanly instead of
              // hanging forever, but keep the tokens intact so the user can retry.
              this.refreshTokenSubject.next(REFRESH_FAILED);
            }
          }
          return throwError(error);
        }

        if (error.status !== 401) {
          return throwError(error);
        }

        if (this.refreshTokenInProgress) {
          // Wait until the in-flight refresh resolves (success token or REFRESH_FAILED sentinel)
          return this.refreshTokenSubject.pipe(
            filter((result) => result !== null),
            take(1),
            switchMap((result) => {
              if (result === REFRESH_FAILED) {
                return throwError(error);
              }
              return next.handle(this.addAuthenticationToken(request));
            }),
          );
        } else {
          this.refreshTokenInProgress = true;
          this.refreshTokenSubject.next(null);

          return this.userService.refreshToken(this.authorizationService.refreshToken).pipe(
            // Retry up to 2 times on network errors (status 0 — transient during
            // WiFi ↔ mobile switches), with a short back-off. Do not retry on
            // 401/403 — those mean the refresh token is genuinely invalid.
            retry({
              count: 2,
              delay: (err: HttpErrorResponse, attempt) => {
                if (err.status === 401 || err.status === 403) {
                  return throwError(() => err);
                }
                return timer(attempt * 1000);
              },
            }),
            tap((tokens) => {
              this.authorizationService.setTokens(tokens.token, tokens.refresh_token);
            }),
            finalize(() => {
              this.refreshTokenInProgress = false;
              // Safety net: if switchMap below never ran (e.g. tap threw), ensure
              // queued requests are never left stuck on null forever.
              if (this.refreshTokenSubject.value === null) {
                this.refreshTokenSubject.next(REFRESH_FAILED);
              }
            }),
            switchMap((token: any) => {
              this.refreshTokenSubject.next(token.token);
              return next.handle(this.addAuthenticationToken(request));
            }),
          );
        }
      }),
    );
  }

  addAuthenticationToken(request) {
    const accessToken = this.authorizationService.jwtToken;
    if (!accessToken) {
      return request;
    }
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }
}
